import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Search, Filter, Download, Eye, Calendar, FileText, Users, 
  Star, ChevronRight, ArrowRight, Clock, BarChart3, 
  TrendingUp, CheckCircle2, X, Bookmark, Share2
} from 'lucide-react';

// Color palette and spacing from Lusidea design system
const COLORS = {
  primary: '#ce2223',
  primaryDark: '#b01a1b',
  primaryLight: '#f8e7e7',
  primaryLighter: '#fef2f2',
  secondary: '#fdb52a',
  secondaryDark: '#e09c18',
  secondaryLight: '#fff8eb',
  secondaryLighter: '#fffbf0',
  black: '#111827',
  darkGray: '#374151',
  mediumGray: '#6b7280',
  lightGray: '#e5e7eb',
  offWhite: '#f9fafb',
  white: '#ffffff',
  success: '#10b981',
  successLight: '#ecfdf5'
};

const SPACING = {
  xs: 'px-3 py-1.5',
  sm: 'px-4 py-2.5',
  md: 'px-6 py-4',
  lg: 'px-8 py-6',
  xl: 'px-12 py-8',
  '2xl': 'px-16 py-12',
  section: 'py-14 md:py-20 lg:py-24',
  sectionMedium: 'py-10 md:py-16 lg:py-20',
  sectionCompact: 'py-8 md:py-12 lg:py-16',
  container: 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'
};

// Simple Button component matching Lusidea design
const Button = ({ children, onClick, variant = 'primary', size = 'md', icon: Icon, className = '', href, ...props }) => {
  const baseClasses = 'inline-flex items-center justify-center font-semibold rounded-lg transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2';
  
  const variants = {
    primary: 'bg-primary text-white hover:bg-primaryDark focus-visible:ring-primary shadow-md hover:shadow-lg',
    secondary: 'bg-secondary text-black hover:bg-secondaryDark focus-visible:ring-secondary shadow-md hover:shadow-lg',
    outline: 'border-2 border-primary text-primary hover:bg-primaryLight focus-visible:ring-primary',
    outlineGray: 'border border-lightGray text-darkGray hover:bg-offWhite focus-visible:ring-mediumGray'
  };
  
  const sizes = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2.5 text-base',
    lg: 'px-6 py-3 text-lg'
  };
  
  const Component = href ? 'a' : 'button';
  const linkProps = href ? { href } : {};
  
  return (
    <Component 
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`}
      onClick={onClick}
      {...linkProps}
      {...props}
    >
      {children}
      {Icon && <Icon size={size === 'lg' ? 20 : size === 'sm' ? 14 : 16} className="ml-2" />}
    </Component>
  );
};

// Simple HeaderNav placeholder
const HeaderNav = () => (
  <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b" style={{ borderColor: COLORS.lightGray }}>
    <div className={SPACING.container}>
      <div className="flex items-center justify-between h-16">
        <div className="flex items-center">
          <a href="/" className="text-xl font-bold" style={{ color: COLORS.primary }}>
            Lusidea
          </a>
        </div>
        <nav className="hidden md:flex items-center space-x-8">
          <a href="/" className="transition-colors" style={{ color: COLORS.darkGray }} onMouseOver={(e) => e.target.style.color = COLORS.primary} onMouseOut={(e) => e.target.style.color = COLORS.darkGray}>Home</a>
          <a href="/services" className="transition-colors" style={{ color: COLORS.darkGray }} onMouseOver={(e) => e.target.style.color = COLORS.primary} onMouseOut={(e) => e.target.style.color = COLORS.darkGray}>Services</a>
          <a href="/reports" className="font-semibold" style={{ color: COLORS.primary }}>Reports</a>
          <a href="/blog" className="transition-colors" style={{ color: COLORS.darkGray }} onMouseOver={(e) => e.target.style.color = COLORS.primary} onMouseOut={(e) => e.target.style.color = COLORS.darkGray}>Blog</a>
          <a href="/contact" className="transition-colors" style={{ color: COLORS.darkGray }} onMouseOver={(e) => e.target.style.color = COLORS.primary} onMouseOut={(e) => e.target.style.color = COLORS.darkGray}>Contact</a>
        </nav>
      </div>
    </div>
  </header>
);

// Simple Footer placeholder
const FooterLan = () => (
  <footer className="py-12" style={{ backgroundColor: COLORS.black, color: COLORS.white }}>
    <div className={SPACING.container}>
      <div className="text-center">
        <div className="text-xl font-bold mb-4" style={{ color: COLORS.primary }}>
          Lusidea
        </div>
        <p className="mb-4" style={{ color: COLORS.mediumGray }}>
          Turn Ideas Into Measurable Impact
        </p>
        <div className="flex justify-center space-x-6 text-sm">
          <a href="/privacy" className="transition-colors" onMouseOver={(e) => e.target.style.color = COLORS.primary} onMouseOut={(e) => e.target.style.color = COLORS.white}>Privacy</a>
          <a href="/terms" className="transition-colors" onMouseOver={(e) => e.target.style.color = COLORS.primary} onMouseOut={(e) => e.target.style.color = COLORS.white}>Terms</a>
          <a href="/contact" className="transition-colors" onMouseOver={(e) => e.target.style.color = COLORS.primary} onMouseOut={(e) => e.target.style.color = COLORS.white}>Contact</a>
        </div>
      </div>
    </div>
  </footer>
);

// Toast Component
const Toast = React.memo(({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 5000);
    
    return () => clearTimeout(timer);
  }, [onClose]);
  
  const bgColor = type === 'success' ? COLORS.successLight : COLORS.primaryLight;
  const textColor = type === 'success' ? COLORS.success : COLORS.primary;
  const icon = type === 'success' ? <CheckCircle2 size={18} /> : <ChevronRight size={18} />;
  
  return (
    <div 
      className="fixed top-20 right-4 z-50 px-4 py-3 rounded-lg shadow-lg border max-w-sm flex items-center gap-3"
      style={{ backgroundColor: bgColor, borderColor: COLORS.lightGray + '20' }}
      role="alert"
      aria-live="assertive"
    >
      <span style={{ color: textColor }} aria-hidden="true">{icon}</span>
      <p className="font-medium" style={{ color: COLORS.darkGray }}>{message}</p>
      <button 
        onClick={onClose} 
        className="ml-auto transition-colors"
        style={{ color: COLORS.mediumGray }}
        onMouseOver={(e) => e.target.style.color = COLORS.primary}
        onMouseOut={(e) => e.target.style.color = COLORS.mediumGray}
        aria-label="Close notification"
      >
        <X size={16} aria-hidden="true" />
      </button>
    </div>
  );
});

Toast.displayName = 'Toast';

// Main Reports Index Component
const ReportsIndex = () => {
  const [reports, setReports] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [toast, setToast] = useState(null);

  // Update page title
  useEffect(() => {
    document.title = 'Strategic Reports & Trend Analysis | Lusidea';
  }, []);

  // Load reports from JSON file
  useEffect(() => {
    const loadReports = async () => {
      try {
        const response = await fetch('/data/reports.json');
        if (!response.ok) throw new Error('Failed to load reports');
        const data = await response.json();
        
        setReports(data.reports || []);
        setCategories(['All', ...(data.categories?.map(cat => cat.name) || [])]);
      } catch (err) {
        setError(err.message);
        setToast({
          message: 'Unable to load reports. Please try again later.',
          type: 'error'
        });
      } finally {
        setLoading(false);
      }
    };
    loadReports();
  }, []);

  // Filter reports based on search and category
  const filteredReports = useMemo(() => {
    return reports.filter(report => {
      const matchesSearch = !searchQuery || 
        report.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        report.shortDescription.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (report.tags && report.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase())));
      
      const matchesCategory = selectedCategory === 'All' || report.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [reports, searchQuery, selectedCategory]);

  // Handle download with toast feedback
  const handleDownload = useCallback((report) => {
    try {
      const link = document.createElement('a');
      link.href = `/pdfs/${report.pdfFilename}`;
      link.download = report.pdfFilename;
      link.click();
      
      setToast({
        message: `Downloading ${report.title}`,
        type: 'success'
      });
    } catch (err) {
      setToast({
        message: 'Download failed. Please try again.',
        type: 'error'
      });
    }
  }, []);

  if (loading) {
    return (
      <div className="font-sans antialiased bg-white overflow-x-hidden min-h-screen" style={{ color: COLORS.darkGray }}>
        <HeaderNav />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 rounded-full animate-spin mx-auto mb-4" style={{ 
              borderColor: COLORS.primary, 
              borderTopColor: 'transparent' 
            }}></div>
            <p style={{ color: COLORS.mediumGray }}>Loading reports...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="font-sans antialiased bg-white overflow-x-hidden min-h-screen" style={{ color: COLORS.darkGray }}>
        <HeaderNav />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center max-w-md mx-auto p-6 bg-white rounded-xl shadow-lg border" style={{ borderColor: COLORS.lightGray }}>
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: COLORS.primaryLight }}>
              <X size={24} style={{ color: COLORS.primary }} />
            </div>
            <h2 className="text-xl font-bold mb-4" style={{ color: COLORS.black }}>Error Loading Reports</h2>
            <p className="mb-4" style={{ color: COLORS.mediumGray }}>{error}</p>
            <Button 
              onClick={() => window.location.reload()}
              variant="primary" 
              size="md"
            >
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="font-sans antialiased bg-white overflow-x-hidden min-h-screen" style={{ color: COLORS.darkGray }}>
      <HeaderNav />
      
      {/* Toast notifications */}
      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}

      <main id="main-content" tabIndex="-1">
        {/* Hero Section */}
        <section className="relative pt-24 sm:pt-28 lg:pt-32 pb-16 md:pb-20 lg:pb-24 overflow-hidden">
          <div 
            className="absolute inset-0 -z-10 opacity-70" 
            style={{
              background: `linear-gradient(135deg, ${COLORS.primaryLight} 0%, ${COLORS.white} 50%, ${COLORS.secondaryLight} 100%)`
            }}
            aria-hidden="true"
          ></div>
          
          <div className={SPACING.container}>
            <div className="text-center max-w-4xl mx-auto">
              <div className="inline-block rounded-full px-4 py-1.5 mb-3" style={{ backgroundColor: COLORS.primaryLight }}>
                <p className="text-sm font-semibold uppercase tracking-wide" style={{ color: COLORS.primary }}>
                  Strategic Intelligence
                </p>
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 leading-tight" style={{ color: COLORS.black }}>
                Strategic <span style={{ color: COLORS.primary }}>Reports</span> & Trend Analysis
              </h1>
              <p className="text-lg sm:text-xl mb-8 max-w-2xl mx-auto leading-relaxed" style={{ color: COLORS.darkGray }}>
                Navigate tomorrow's opportunities with our comprehensive collection of strategic insights, trend analysis, and future intelligence reports.
              </p>
              
              <div className="flex items-center justify-center gap-8 text-sm" style={{ color: COLORS.mediumGray }}>
                <div className="flex items-center gap-2">
                  <FileText size={16} style={{ color: COLORS.primary }} />
                  <span>{reports.length} Reports Available</span>
                </div>
                <div className="flex items-center gap-2">
                  <TrendingUp size={16} style={{ color: COLORS.secondary }} />
                  <span>Updated Monthly</span>
                </div>
                <div className="flex items-center gap-2">
                  <Download size={16} style={{ color: COLORS.primary }} />
                  <span>Free Downloads</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Search and Filters Section */}
        <section className="py-12" style={{ backgroundColor: COLORS.offWhite }}>
          <div className={SPACING.container}>
            <div className="bg-white rounded-xl shadow-lg p-6 border" style={{ borderColor: COLORS.lightGray }}>
              <div className="flex flex-col lg:flex-row gap-6">
                {/* Search */}
                <div className="flex-1">
                  <div className="relative">
                    <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: COLORS.mediumGray }} />
                    <input
                      type="text"
                      placeholder="Search reports by title, description, or tags..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent"
                      style={{ 
                        borderColor: COLORS.lightGray,
                        color: COLORS.darkGray,
                        '--tw-ring-color': COLORS.primary
                      }}
                    />
                  </div>
                </div>
                
                {/* Category Filter */}
                <div className="lg:w-64">
                  <div className="relative">
                    <Filter size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: COLORS.mediumGray }} />
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="w-full pl-10 pr-8 py-3 border rounded-lg focus:ring-2 focus:border-transparent appearance-none bg-white"
                      style={{ 
                        borderColor: COLORS.lightGray,
                        color: COLORS.darkGray,
                        '--tw-ring-color': COLORS.primary
                      }}
                    >
                      {categories.map(category => (
                        <option key={category} value={category}>
                          {category === 'All' ? 'All Categories' : category}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              
              {/* Results count and clear filters */}
              <div className="flex items-center justify-between mt-6 pt-6 border-t" style={{ borderColor: COLORS.lightGray }}>
                <div className="text-sm" style={{ color: COLORS.mediumGray }}>
                  <span className="font-semibold" style={{ color: COLORS.black }}>{filteredReports.length}</span> {filteredReports.length === 1 ? 'report' : 'reports'} found
                  {(searchQuery || selectedCategory !== 'All') && (
                    <span className="ml-2">
                      for <span className="font-medium" style={{ color: COLORS.primary }}>
                        {searchQuery && `"${searchQuery}"`}
                        {searchQuery && selectedCategory !== 'All' && ' in '}
                        {selectedCategory !== 'All' && selectedCategory}
                      </span>
                    </span>
                  )}
                </div>
                
                {(searchQuery || selectedCategory !== 'All') && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedCategory('All');
                    }}
                    className="text-sm font-medium flex items-center gap-1 transition-colors"
                    style={{ color: COLORS.primary }}
                    onMouseOver={(e) => e.target.style.color = COLORS.primaryDark}
                    onMouseOut={(e) => e.target.style.color = COLORS.primary}
                  >
                    <X size={14} />
                    Clear filters
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Reports Grid Section */}
        <section className={SPACING.sectionMedium}>
          <div className={SPACING.container}>
            {filteredReports.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: COLORS.lightGray }}>
                  <Search size={24} style={{ color: COLORS.mediumGray }} />
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ color: COLORS.black }}>No reports found</h3>
                <p className="mb-6" style={{ color: COLORS.mediumGray }}>
                  Try adjusting your search criteria or browse all available reports.
                </p>
                <Button 
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedCategory('All');
                  }}
                  variant="primary" 
                  size="md"
                >
                  Show All Reports
                </Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredReports.map(report => (
                  <ReportCard 
                    key={report.id} 
                    report={report} 
                    onDownload={() => handleDownload(report)}
                    onViewDetails={() => window.location.href = `/reports/${report.slug}`}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <FooterLan />
    </div>
  );
};

// Report Card Component
const ReportCard = React.memo(({ report, onDownload, onViewDetails }) => {
  const formatDate = useCallback((dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (e) {
      return dateStr;
    }
  }, []);

  const getCategoryColor = useCallback((category) => {
    const colorMap = {
      'Technology Trends': { bg: '#dbeafe', text: '#1d4ed8' },
      'Sustainability Trends': { bg: '#dcfce7', text: '#166534' },
      'Workplace Trends': { bg: '#f3e8ff', text: '#7c3aed' },
      'Consumer Trends': { bg: '#fef3c7', text: '#d97706' },
      'Innovation Trends': { bg: COLORS.primaryLight, text: COLORS.primary },
      default: { bg: COLORS.secondaryLight, text: COLORS.secondaryDark }
    };
    return colorMap[category] || colorMap.default;
  }, []);

  const categoryColors = getCategoryColor(report.category);

  return (
    <article className="bg-white rounded-xl shadow-lg border overflow-hidden hover:shadow-xl transition-all duration-300 flex flex-col h-full" style={{ borderColor: COLORS.lightGray }}>
      {/* Cover Image */}
      <div 
        className="h-48 flex items-center justify-center relative"
        style={{
          background: `linear-gradient(135deg, ${COLORS.primaryLight} 0%, ${COLORS.secondaryLight} 100%)`
        }}
      >
        <div className="text-center">
          <div className="w-16 h-16 bg-white/90 rounded-xl flex items-center justify-center mb-3 shadow-md">
            <FileText size={32} style={{ color: COLORS.primary }} />
          </div>
          <div className="bg-white/90 rounded-lg px-3 py-2 max-w-[80%]">
            <p className="text-xs font-semibold line-clamp-2" style={{ color: COLORS.black }}>
              {report.title.length > 40 ? report.title.substring(0, 40) + '...' : report.title}
            </p>
          </div>
        </div>
        
        {/* Featured Badge */}
        {report.featured && (
          <div className="absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1" style={{ backgroundColor: COLORS.secondary, color: COLORS.black }}>
            <Star size={12} fill="currentColor" />
            Featured
          </div>
        )}

        {/* Premium Badge */}
        {report.premium && (
          <div className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: COLORS.primary, color: COLORS.white }}>
            Premium
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6 flex flex-col flex-grow">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-semibold px-3 py-1 rounded-full" style={{ backgroundColor: categoryColors.bg, color: categoryColors.text }}>
            {report.category}
          </span>
          <span className="text-xs flex items-center gap-1" style={{ color: COLORS.mediumGray }}>
            <Calendar size={12} />
            {formatDate(report.publishedDate)}
          </span>
        </div>

        <h3 className="text-lg font-bold mb-3 line-clamp-2 flex-grow" style={{ color: COLORS.black }}>
          {report.title}
        </h3>

        <p className="text-sm mb-4 line-clamp-3" style={{ color: COLORS.darkGray }}>
          {report.shortDescription}
        </p>

        {/* Stats */}
        <div className="flex items-center justify-between text-xs mb-6 py-3 border-t" style={{ color: COLORS.mediumGray, borderColor: COLORS.lightGray }}>
          <span className="flex items-center gap-1">
            <FileText size={12} />
            {report.pageCount} pages
          </span>
          <span className="flex items-center gap-1">
            <BarChart3 size={12} />
            {report.fileSize}
          </span>
          <span className="flex items-center gap-1">
            <Download size={12} />
            {(report.downloadCount || 0).toLocaleString()}
          </span>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-auto">
          <button
            onClick={onViewDetails}
            className="flex-1 px-4 py-2.5 text-sm font-semibold border-2 rounded-lg transition-all duration-300"
            style={{ 
              borderColor: COLORS.primary, 
              color: COLORS.primary 
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = COLORS.primaryLight}
            onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            View Details
          </button>
          <button
            onClick={onDownload}
            className="px-4 py-2.5 rounded-lg transition-colors duration-300 flex items-center justify-center"
            style={{ backgroundColor: COLORS.primary, color: COLORS.white }}
            onMouseOver={(e) => e.target.style.backgroundColor = COLORS.primaryDark}
            onMouseOut={(e) => e.target.style.backgroundColor = COLORS.primary}
            aria-label={`Download ${report.title}`}
          >
            <Download size={16} />
          </button>
        </div>
      </div>
    </article>
  );
});

ReportCard.displayName = 'ReportCard';

export default ReportsIndex;